/**
 * E.4 — Vault outbound status surface.
 *
 * Mounts inside `MicrosoftAccountPanel`. Reads:
 *   - `api.vaultOutboundQueue.counts`      — pending / in-flight / failed / needs-resolution
 *   - `api.vaultOutboundQueue.listForSlug` — full rows so we can find the first needs-resolution
 *
 * Renders:
 *   - Status summary (counts)
 *   - "Force sync now" button → calls `drainOutbound` from `lib/graph/syncDriveOutbound.ts`
 *   - On a `needs-resolution` row: mounts `ConflictDialog` and wires `onResolve`
 *     → `api.vaultOutboundQueue.resolveConflict` mutation
 *
 * Per-drive heartbeat (`sharepointSyncState.getState`) is NOT surfaced here —
 * that query needs a known `driveId`. Future: when E.4 is mounted alongside a
 * drive picker, add the heartbeat. Tracked as future work.
 *
 * Conventions (per memory):
 *   - Tailwind only (`feedback_use_tailwind`)
 *   - Black text on light bg (`feedback_no_light_grey_text`) — gray-700/800 max
 *   - data-testid from `testIds.ts` (`feedback_data_testid`)
 */

import { useCallback, useState, type ReactElement } from 'react';
import { useConvex, useQuery } from 'convex/react';
import { api } from '../../../convex/_generated/api';
import { useOptionalTenantBranding } from '../../lib/tenant/TenantBrandingContext';
import { drainOutbound } from '../../lib/graph/syncDriveOutbound';
import { getPendingWriteBody } from '../../lib/nipe/pendingWriteBodies';
import type { ConflictResolution } from '../../lib/graph/saveBack';
import { ConflictDialog } from './ConflictDialog';
import { testIds } from '../../lib/testIds';

interface QueueCounts {
  pending: number;
  inFlight: number;
  completed: number;
  failed: number;
  needsResolution: number;
}

interface OutboundRow {
  _id: string;
  notePath: string;
  status: string;
  conflictRemoteEtag?: string;
  lastErrorMessage?: string;
}

/**
 * Pick a `clientSlug` for the queue reads. In production, the panel
 * mounts inside a portal route so `useOptionalTenantBranding` returns
 * the real slug. In `?test=1` mode (J.12 vault-outbound-sync spec)
 * there's no portal — fall back to the same `'test'` slug that the
 * `__repancyTestEnqueueOutbound` hook uses, so the panel sees the
 * same rows the hook is creating. Production users without portal
 * scope still see nothing (the `!clientSlug` branch below returns null).
 */
function resolveSlug(brandingSlug: string | undefined): string {
  if (brandingSlug) return brandingSlug;
  if (typeof window === 'undefined') return '';
  try {
    if (new URLSearchParams(window.location.search).get('test') === '1') {
      return (
        new URLSearchParams(window.location.search).get('clientSlug') ?? 'test'
      );
    }
  } catch {
    /* SSR / sandbox — fall through to empty */
  }
  return '';
}

export function VaultOutboundStatus(): ReactElement | null {
  const ctx = useOptionalTenantBranding();
  const clientSlug = resolveSlug(ctx?.slug);
  const convex = useConvex();

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Counts subscription — undefined while in flight, shape when ready.
  const counts = useQuery(
    api.vaultOutboundQueue.counts,
    clientSlug
      ? { clientSlug }
      : 'skip',
  ) as QueueCounts | undefined;

  // Rows — used to find the first `needs-resolution` row for ConflictDialog.
  const rows = useQuery(
    api.vaultOutboundQueue.listForSlug,
    clientSlug
      ? { clientSlug }
      : 'skip',
  ) as OutboundRow[] | undefined;

  const conflictRow = rows?.find((r) => r.status === 'needs-resolution');

  const handleForceSync = useCallback(async () => {
    if (!clientSlug) return;
    setBusy(true);
    setError(null);
    try {
      // Live-mode saveBack needs a `notePath → SharePointExternalRef`
      // resolver that doesn't exist yet (deferred to
      // F-OUTBOUND-SAVEBACK-RESOLVER in long-tail). Until then, we pass
      // `null` saveBack; the drain worker marks rows completed
      // synthetically so the status surface still reacts to the user's
      // clicks.
      await drainOutbound(
        convex,
        { vaultOutboundQueue: api.vaultOutboundQueue },
        null,
        (id) => getPendingWriteBody(id),
        { clientSlug },
        {
          maxPerCycle: 5,
        },
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }, [clientSlug, convex]);

  const handleResolve = useCallback(
    async (resolution: ConflictResolution) => {
      if (!conflictRow) return;
      try {
        await convex.mutation(api.vaultOutboundQueue.resolveConflict, {
          queueId: conflictRow._id,
          resolution,
        });
        // If user chose overwrite, the row resets to pending — kick a
        // drain immediately so the dialog disappears + the row clears.
        if (resolution === 'overwrite') {
          void handleForceSync();
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      }
    },
    [conflictRow, convex, handleForceSync],
  );

  // Hide entirely outside portal scope (no slug, no queue).
  if (!clientSlug) return null;

  const pending = counts?.pending ?? 0;
  const inFlight = counts?.inFlight ?? 0;
  const failed = counts?.failed ?? 0;
  const needsResolution = counts?.needsResolution ?? 0;

  return (
    <div
      data-testid={testIds.microsoftAccount.outboundStatusSection}
      className="mt-3 mb-2 p-2 bg-white border border-gray-700 text-black"
    >
      <div className="text-xs font-bold mb-1">Vault → SharePoint sync</div>

      <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-xs mb-2">
        <span
          data-testid={testIds.microsoftAccount.outboundCountsPending}
          data-count={pending}
        >
          Pending: <strong>{pending}</strong>
        </span>
        <span
          data-testid={testIds.microsoftAccount.outboundCountsInFlight}
          data-count={inFlight}
        >
          In flight: <strong>{inFlight}</strong>
        </span>
        <span
          data-testid={testIds.microsoftAccount.outboundCountsFailed}
          data-count={failed}
          className={failed > 0 ? 'text-red-800' : ''}
        >
          Failed: <strong>{failed}</strong>
        </span>
        <span
          data-testid={testIds.microsoftAccount.outboundCountsNeedsResolution}
          data-count={needsResolution}
          className={needsResolution > 0 ? 'text-red-800' : ''}
        >
          Needs resolution: <strong>{needsResolution}</strong>
        </span>
      </div>

      <div className="flex gap-2 mt-1">
        <button
          type="button"
          data-testid={testIds.microsoftAccount.outboundForceSyncButton}
          onClick={handleForceSync}
          disabled={busy || (pending === 0 && needsResolution === 0)}
          className="px-2 py-0.5 text-xs bg-gray-200 hover:bg-gray-300 border border-black text-black disabled:text-gray-700 disabled:cursor-default"
        >
          {busy ? 'Syncing…' : 'Force sync now'}
        </button>
      </div>

      {error !== null && (
        <div className="text-xs text-red-800 mt-1">{error}</div>
      )}

      <ConflictDialog
        open={conflictRow !== undefined}
        documentTitle={conflictRow?.notePath ?? ''}
        remoteAuthor={undefined}
        onResolve={handleResolve}
        onCancel={() => {
          /* Cancel from overlay click = keep needs-resolution state; user
             can come back to it. Dialog will simply close until next
             render cycle. */
        }}
      />
    </div>
  );
}

